<script setup lang="ts">
import type { Product } from "~/stores/products";
import { clampSplitQuantityModifiers } from "~/utils/clampSplitQuantityModifiers";
import { scaleModifiersToLine, modifiersLineTotal } from "~/utils/lineModifierQuantities";
import { STORED_CART_KEY } from "~/stores/orders";
import { attributeTagVariant, compareAttributeTags } from "~/utils/attributeTagVariant";
import { isNewMaker } from "~/utils/isNewMaker";
import { Z_FILTER_FIXED, Z_MODAL, Z_CART_FIXED, Z_CART_BEHIND_MODAL } from "~/utils/zIndex";

const route = useRoute();
const router = useRouter();
const makersStore = useMakersStore();
const productsStore = useProductsStore();
const reviewsStore = useReviewsStore();
const ordersStore = useOrdersStore();
const { attachmentUrl, staticUrl } = useImageUrl();
const { track } = useTracking();

definePageMeta({
  layout: "default",
});

const slug = computed(() => route.params.slug as string);
const productSlug = computed(() => (route.params.productSlug as string) || "");

// Fetched during SSR, not in onMounted. Previously every crawler — and every social
// scraper — received the fallback title, an empty <h1> and an empty JSON-LD block,
// because nothing on this page was resolved server-side. The nested product route
// depends on this too: without it the 404 for an unknown dish slug could only happen
// client-side, leaving a soft-200 under a product canonical.
//
// suppressRedirect is required: getMakerBySlug's catch calls navigateTo("/404"),
// which is wrong inside an SSR handler. Returning a slim value keeps the maker out of
// the payload twice — Pinia state is already serialised by @pinia/nuxt.
//
// Exactly ONE owner of this fetch, deliberately:
//   - first render (server + initial client): this useAsyncData
//   - every later slug change: the watch(slug) handler below, via `await refresh()`
//
// Hence the STATIC key and no `watch: [slug]`. A reactive key or a `watch` option
// would refetch on slug change *without* the watcher awaiting it, and the watcher's
// very next lines read `chosenMaker.value.id` — so it would run `getDeliveryDays`
// and the product resolution against the PREVIOUS maker, putting maker-a's delivery
// days on maker-b's page and feeding them into cart date selection.
const { refresh: refreshMaker, error: makerError } = await useAsyncData("store-maker", async () => {
  await makersStore.getMakerBySlug(
    { slug: slug.value, makerPage: true },
    { suppressRedirect: true },
  );
  if (!makersStore.chosenMaker) {
    throw createError({ statusCode: 404, statusMessage: "Maker not found" });
  }
  return true;
});

if (makerError.value) throw makerError.value;

// Resolve against the maker payload, not the display-filtered store list.
// Inactive categories are stripped from `productCategories` for the menu, but
// active bundles from those categories still render under Set Menus — looking
// at the filtered list 404s their /product/{slug} URL on refresh.
const activeProduct = computed(() =>
  productSlug.value
    ? findProductBySlug(makersStore.chosenMaker?.productCategories, productSlug.value)
    : null,
);

// A product URL that resolves to nothing must 404, not quietly render the store page
// under a product canonical — that invites indexing of unbounded typo'd URLs.
if (productSlug.value && makersStore.chosenMaker && !activeProduct.value) {
  throw createError({ statusCode: 404, statusMessage: "Product not found" });
}

// Legacy `?product={id}` links (shared before dishes had their own URLs) redirect to
// the canonical path. This runs during setup, not onMounted, so crawlers and social
// scrapers get a real server-side 301 rather than a 200 that only rewrites the URL
// after hydration. No extra request: the maker payload already has every id + slug.
if (import.meta.server && route.query.product && makersStore.chosenMaker) {
  const legacyMatch = findProductById(
    makersStore.chosenMaker?.productCategories,
    route.query.product as string,
  );
  if (legacyMatch && hasCanonicalProductPath(legacyMatch)) {
    await navigateTo(productPath(slug.value, legacyMatch), {
      redirectCode: 301,
      replace: true,
    });
  }
}

// Per-page share image: the dish on a product URL, else the maker's profile shot.
// Every page previously inherited one global meal-box photo, so every Slack,
// WhatsApp and AI preview of every maker and every dish looked identical.
// `undefined` leaves the global og:image in place rather than emitting an empty tag.
// (`absoluteAttachmentUrl` is a hoisted function declaration, so it is safe to call
// from here even though it is written further down.)
const shareImage = computed<string | undefined>(() => {
  if (activeProduct.value) {
    const productImage = absoluteAttachmentUrl(activeProduct.value.attachments, "mainImage");
    if (productImage) return productImage;
  }
  return (
    absoluteAttachmentUrl(makersStore.chosenMaker?.attachments, "profile") ||
    absoluteAttachmentUrl(makersStore.chosenMaker?.attachments, "banner")
  );
});

useSeoMeta({
  title: () => {
    if (activeProduct.value) {
      return `${activeProduct.value.name} — ${makersStore.chosenMaker?.name ?? "&Dine"} | &Dine`;
    }
    return makersStore.chosenMaker ? `${makersStore.chosenMaker.name} from &Dine` : "Maker | &Dine";
  },
  description: () =>
    stripHtml(
      (activeProduct.value?.description as string) ||
        makersStore.chosenMaker?.description ||
        "Office catering in London, done right. Quality food made by independent Makers and delivered directly to your team.",
    ).slice(0, 160),
  ogImage: () => shareImage.value,
  twitterImage: () => shareImage.value,
});

const siteOrigin = useRuntimeConfig().public.mainUrl as string;

// Self-referential on both routes. On a product URL this must point at the product,
// not back at the store page — a sitemap entry whose canonical points elsewhere is
// consolidated away, which is exactly why the old ?product= URLs never indexed.
const canonicalUrl = computed(() =>
  activeProduct.value
    ? `${siteOrigin}${productPath(slug.value, activeProduct.value as { id: string | number; slug?: string | null })}`
    : `${siteOrigin}/store/${slug.value}`,
);

// `attachments.<collection>` is an array of objects served from apiBase, and
// AttachmentService defaults missing collections to `[]` (which is truthy) — so the
// previous `https://image.anddine.co.uk/${maker.attachments.profile}` emitted
// "[object Object]" even when there was no image. Empty fallback so an absent image
// is omitted rather than published as a placeholder.
function absoluteAttachmentUrl(attachments: unknown, collection: string): string | undefined {
  const collections = attachments as Record<string, unknown> | undefined;
  const files = collections?.[collection];
  if (!Array.isArray(files) || !files.length) return undefined;
  return attachmentUrl(files, "big", "") || undefined;
}

// schema.org wants HH:MM. `open_time`/`close_time` are plain strings with no
// enforced format, so validate rather than trust — a malformed value would
// invalidate the whole node.
const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/;

const makerCuisines = computed<string[]>(() => {
  const cuisines = makersStore.chosenMaker?.cuisines;
  if (!Array.isArray(cuisines)) return [];
  return (cuisines as { name?: string }[]).map((c) => c?.name).filter((n): n is string => !!n);
});

const makerAttributeTags = computed(() => {
  const maker = makersStore.chosenMaker;
  const tags = maker ? maker.tags : undefined;
  if (!Array.isArray(tags)) return [];
  return tags.filter((tag) => tag.category === "attribute").sort(compareAttributeTags);
});

const makerIsNew = computed(() =>
  isNewMaker(makersStore.chosenMaker ? makersStore.chosenMaker.createdAt : undefined),
);

const openingHours = computed(() => {
  const days = makersStore.chosenMaker?.deliveryDays;
  if (!Array.isArray(days)) return undefined;

  const spec = (days as Record<string, unknown>[])
    .filter(
      (d) =>
        d?.is_active &&
        typeof d.name === "string" &&
        HH_MM.test(String(d.open_time)) &&
        HH_MM.test(String(d.close_time)),
    )
    .map((d) => ({
      "@type": "OpeningHoursSpecification",
      // `name` is already the full English day name, so this maps straight onto
      // schema.org's day URLs.
      dayOfWeek: `https://schema.org/${d.name}`,
      opens: d.open_time,
      closes: d.close_time,
    }));

  return spec.length ? spec : undefined;
});

// Derived from the rendered menu so it can't disagree with what's on the page.
const priceRange = computed(() => {
  const cats = productsStore.productCategories;
  if (!Array.isArray(cats)) return undefined;

  const prices = (
    cats as { products?: { is_active?: boolean; future_day?: boolean; price_gross?: number }[] }[]
  )
    .flatMap((c) => c.products ?? [])
    .filter((p) => p?.is_active && p?.future_day && Number.isFinite(Number(p.price_gross)))
    .map((p) => Number(p.price_gross));

  if (!prices.length) return undefined;
  // Money is integer pence.
  const min = Math.min(...prices) / 100;
  const max = Math.max(...prices) / 100;
  return min === max ? `£${min.toFixed(2)}` : `£${min.toFixed(2)}–£${max.toFixed(2)}`;
});

// Only emitted once the backend actually sends an aggregate. Guarded on count > 0
// so an unreviewed maker never advertises a 0-star rating, and written defensively
// so the site is correct whether or not the backend change is deployed yet.
const aggregateRating = computed(() => {
  const maker = makersStore.chosenMaker as Record<string, unknown> | false;
  if (!maker) return undefined;

  const count = Number(maker.reviewCount ?? 0);
  const average = Number(maker.reviewAverage ?? NaN);
  if (!Number.isFinite(count) || count < 1 || !Number.isFinite(average)) return undefined;

  return {
    "@type": "AggregateRating",
    ratingValue: average,
    reviewCount: count,
    bestRating: 5,
    worstRating: 1,
  };
});

const menuGraphNodes = computed(() => {
  const maker = makersStore.chosenMaker;
  if (!maker) return [];

  const makerUrl = `${siteOrigin}/store/${slug.value}`;
  const menuId = `${makerUrl}#menu`;
  const cats = Array.isArray(productsStore.productCategories)
    ? productsStore.productCategories
    : [];

  const sections = cats
    .map((cat) => {
      const products = (cat.products ?? []).filter(
        (p) => p?.is_active && p?.future_day && hasCanonicalProductPath(p),
      );
      if (!products.length) return null;

      return {
        "@type": "MenuSection",
        "@id": `${makerUrl}#section-${cat.id}`,
        name: cat.name,
        description: stripHtml((cat as { description?: string }).description) || undefined,
        hasMenuItem: products.map((product) => {
          const pence = Number(product.price_gross);
          return {
            "@type": "MenuItem",
            name: product.name,
            description: stripHtml(product.description) || undefined,
            url: `${siteOrigin}${productPath(slug.value, product)}`,
            offers: Number.isFinite(pence)
              ? {
                  "@type": "Offer",
                  price: (pence / 100).toFixed(2),
                  priceCurrency: "GBP",
                  availability: "https://schema.org/InStock",
                }
              : undefined,
          };
        }),
      };
    })
    .filter((section): section is NonNullable<typeof section> => section != null);

  if (!sections.length) return [];

  return [
    {
      "@type": "Menu",
      "@id": menuId,
      name: `${maker.name} menu`,
      hasMenuSection: sections.map((section) => ({ "@id": section["@id"] })),
    },
    ...sections,
  ];
});

const storeSchemaNodes = computed(() => {
  const maker = makersStore.chosenMaker;
  if (!maker) return [];

  const makerUrl = `${siteOrigin}/store/${slug.value}`;
  const nodes: Array<Record<string, unknown> | null | undefined> = [
    {
      "@type": "FoodEstablishment",
      "@id": `${makerUrl}#maker`,
      name: maker.name,
      description: stripHtml(maker.description as string) || undefined,
      url: makerUrl,
      image: absoluteAttachmentUrl(maker.attachments, "profile"),
      servesCuisine: Array.isArray(maker.cuisines)
        ? (maker.cuisines as { name?: string }[]).map((c) => c?.name).filter(Boolean)
        : undefined,
      address: {
        "@type": "PostalAddress",
        addressLocality: "London",
        addressCountry: "GB",
      },
      areaServed: { "@type": "City", name: "London", addressCountry: "GB" },
      openingHoursSpecification: openingHours.value,
      priceRange: priceRange.value,
      dateModified: maker.updatedAt || undefined,
      hasMenu: menuGraphNodes.value.length ? { "@id": `${makerUrl}#menu` } : undefined,
      publisher: { "@id": `${siteOrigin}/#organization` },
      // aggregateRating comes from the backend aggregate added alongside this
      // (`reviewAverage`/`reviewCount` on the public maker read). It is NOT the
      // client-side mean from reviewsStore, which isn't resolved during SSR —
      // and the old code read a field the API never sent, so this block has
      // never actually emitted a rating before now.
      aggregateRating: aggregateRating.value,
    },
    ...menuGraphNodes.value,
  ];

  const product = activeProduct.value;
  if (product) {
    const pence = Number(product.price_gross);
    nodes.push({
      "@type": "Product",
      "@id": `${canonicalUrl.value}#product`,
      name: product.name,
      description: stripHtml(product.description as string) || undefined,
      image: absoluteAttachmentUrl(product.attachments, "mainImage"),
      brand: { "@id": `${makerUrl}#maker` },
      offers: Number.isFinite(pence)
        ? {
            "@type": "Offer",
            price: (pence / 100).toFixed(2),
            priceCurrency: "GBP",
            availability: "https://schema.org/InStock",
            url: canonicalUrl.value,
            seller: { "@id": `${makerUrl}#maker` },
          }
        : undefined,
    });
  }

  // Home → Browse Makers → {maker} → {dish}. The last crumb has no `item` per
  // Google's guidance (the current page is not a link to itself).
  const crumbs: Record<string, unknown>[] = [
    { "@type": "ListItem", position: 1, name: "Home", item: siteOrigin },
    { "@type": "ListItem", position: 2, name: "Browse Makers", item: `${siteOrigin}/search` },
  ];

  if (product) {
    crumbs.push({
      "@type": "ListItem",
      position: 3,
      name: maker.name,
      item: makerUrl,
    });
    crumbs.push({ "@type": "ListItem", position: 4, name: product.name });
  } else {
    crumbs.push({ "@type": "ListItem", position: 3, name: maker.name });
  }

  nodes.push({
    "@type": "BreadcrumbList",
    "@id": `${canonicalUrl.value}#breadcrumb`,
    itemListElement: crumbs,
  });

  return nodes;
});

useHead({
  link: [{ rel: "canonical", href: canonicalUrl }],
});

useSchemaGraph("ld-store", storeSchemaNodes);

const isMobile = ref(false);
const lastScrolled = ref<any>(false);
const showProductModal = ref(false);
const showMakerModal = ref(false);
const showShareModal = ref(false);
const showVerifyModal = ref(false);
const modalQuantity = ref(1);
const minQuantity = ref(1);
const selectedModifiers = ref<any[]>([]);
const isCopied = ref(false);
// Starts true so the first paint is the spinner, not an empty maker. The store's
// `makerLoading` defaults to false and the fetch only starts in onMounted, so gating
// on it alone renders the header with `chosenMaker` still `{}` — the banner falls back
// to the placeholder, paints, then swaps to the real image once the fetch resolves.
const pageLoading = ref(true);
const pendingItem = ref<any>(false);
const resultLength = ref(0);
const allergenModalVisible = ref(false);
const isFilterFixed = ref(false);
const isCartFixed = ref(false);
const filterOffsetTop = ref(0);
const cartOffsetTop = ref(0);
const filterHeight = ref(0);
const cartHeight = ref(0);
const filterGap = 12;
const cartLeftPx = ref(0);
const cartWidthPx = ref(0);
const cartMaxHeight = ref("none");
const cartStopped = ref(false);
const cartStoppedTop = ref(0);
const cartTopPx = ref(0);
const selectedTemperature = ref<boolean | null>(null);
const bioExpanded = ref(false);

const filtersRef = ref<HTMLElement | null>(null);
const cartRef = ref<HTMLElement | null>(null);
const scrollContainerRef = ref<HTMLElement | null>(null);
const makersRef = ref<HTMLElement | null>(null);
const bioTextRef = ref<HTMLElement | null>(null);
// Whether the 5-line clamp is actually cutting the bio off. Measured from the DOM
// because the clamp is a CSS line count — a character-length guess can't know the
// rendered wrap at a given viewport width, and a short bio must not show a
// "Read more" that reveals nothing.
const bioOverflowing = ref(false);

function pxToRem(px: number): number {
  const fontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  return +(px / fontSize).toFixed(4);
}

const chosenMaker = computed(() => makersStore.chosenMaker || ({} as Record<string, any>));
const chosenProduct = computed(() => productsStore.chosenProduct || ({} as Record<string, any>));

const reviewCount = computed(() => {
  if (!reviewsStore.reviews) return false;
  const relevant = reviewsStore.reviews.filter((r: any) => r.maker_slug === route.params.slug);
  const total = relevant.reduce((sum: number, r: any) => sum + r.score, 0);
  return relevant.length ? (total / relevant.length).toFixed(1) : false;
});

const categoryItems = computed(() =>
  Array.isArray(productsStore.productCategories)
    ? productsStore.productCategories
        .filter((cat: any) => cat.products.some((p: any) => p.future_day))
        .map((cat: any) => ({
          slug: cat.slug
            .toLowerCase()
            .replace(/[^\w\s]/gi, "")
            .trim()
            .replace(/\s+/g, "-"),
          name: cat.name,
        }))
    : [],
);

const hasAdditionalImages = computed(() => chosenProduct.value.attachments?.mainImage?.length > 1);

const hasDishImage = computed(
  () => chosenProduct.value.attachments?.mainImage?.length > 0 && !hasAdditionalImages.value,
);

const dishModifiers = computed(
  () =>
    chosenProduct.value?.modifierGroups?.filter(
      (g: any) => g.is_active && g.modifiers.length > 0,
    ) || false,
);

// Each modifier group's minimum has to be met by that group's OWN selections.
//
// The previous gate compared one cross-group aggregate — sum(selectedModifiers.quantity) — against
// another, sum(group.min). A surplus in one group could satisfy the total on behalf of an empty one:
// two required groups at min 1 each, two options picked in group A, 2 >= 2, Add enabled with group B
// untouched. Counting selections instead of summing quantities would not have helped; the flaw is the
// cross-group comparison itself. Nothing catches it server-side — the backend does not validate
// modifierGroups.min on any order path.
//
// Split-ness is read from the GROUP, not from the selected entry: this only runs with a modal open, so
// dishModifiers is always live product data, and bucketing keys on modifier_group_id.
const requiredModifierGroupsSatisfied = computed(() => {
  const groups = dishModifiers.value;
  if (!groups || !groups.length) return true;

  const selected = selectedModifiers.value || [];

  return groups.every((group: any) => {
    const picks = selected.filter(
      (mod: any) => mod.modifier_group_id === group.id && Number(mod.quantity) > 0,
    );
    if (picks.length < Number(group.min || 0)) return false;

    // A split group's shares must also sum to the line quantity, mirroring the server-side
    // validateSplitQuantityModifiers — whose ModSplitQtyMismatch is not client-facing, so failing it
    // surfaces as an opaque error rather than something the customer can act on.
    if (group.split_quantity === true && picks.length) {
      const shareTotal = picks.reduce((acc: number, mod: any) => acc + Number(mod.quantity), 0);
      if (shareTotal !== Number(modalQuantity.value)) return false;
    }

    return true;
  });
});

const addButtonDisabled = computed(() => !requiredModifierGroupsSatisfied.value);

// Pounds, WHOLE LINE. The split-aware rule and why the line multiplier belongs in the helper rather
// than in combinedProductTotal are documented in utils/lineModifierQuantities.ts. Never multiply the
// base and the add-ons together as one per-unit figure: a split share already covers the line.
const modifiersLineTotalPounds = computed(() =>
  modifiersLineTotal(selectedModifiers.value as any[], modalQuantity.value || 1, (a: any) =>
    Number(a.price_gross || 0),
  ),
);

const combinedProductTotal = computed(() =>
  (
    ((chosenProduct.value?.price_gross || 0) / 100) * (modalQuantity.value || 1) +
    modifiersLineTotalPounds.value
  ).toFixed(2),
);

function getProductAttribute(name: string) {
  return (
    chosenProduct.value?.productAttributes?.find((a: any) => a.name === name)?.description || false
  );
}

const cookingInstructions = computed(() => getProductAttribute("Cooking instructions"));
const ingredients = computed(() => getProductAttribute("Ingredients"));
const shelfLife = computed(() => getProductAttribute("Shelf life"));
const servingSuggestions = computed(() => getProductAttribute("Serving suggestions"));
const additionalInformation = computed(() => getProductAttribute("Additional information"));
const storage = computed(() => getProductAttribute("Storage"));
const faqs = computed(() => chosenProduct.value?.productAttributes?.faq || false);
const servings = computed(() =>
  chosenProduct.value?.servings > 0 ? `Serves ${chosenProduct.value.servings}` : false,
);
const pieces = computed(() =>
  chosenProduct.value?.pieces ? `${chosenProduct.value.pieces} pieces` : false,
);

const finalFilteredCategories = computed(() => {
  const filtered = productsStore.filteredProductCategories;
  const base =
    filtered && filtered.length
      ? filtered
      : Array.isArray(productsStore.productCategories)
        ? productsStore.productCategories
        : [];

  // The is_active / future_day gate is applied on BOTH paths. The store's dietary
  // filter is generic (the order-edit page shares it) and only narrows by allergens,
  // so applying a chip must not resurrect an inactive or non-orderable product that
  // the unfiltered list hides.
  return base
    .map((cat: any) => ({
      ...cat,
      products: (cat.products || []).filter((p: any) => {
        if (!p.is_active || !p.future_day) return false;
        if (selectedTemperature.value === null) return true;
        return selectedTemperature.value === true ? !!p.is_hot : !p.is_hot;
      }),
    }))
    .filter((cat: any) => cat.products.length > 0);
});

watch(finalFilteredCategories, (to) => {
  resultLength.value =
    to && to.length > 0
      ? to.reduce(
          (acc: number, cat: any) => acc + cat.products.filter((p: any) => p.is_active).length,
          0,
        )
      : 0;
});

onMounted(async () => {
  isMobile.value = window.innerWidth <= 768;
  window.addEventListener("resize", () => {
    isMobile.value = window.innerWidth <= 768;
  });

  try {
    await makersStore.getMakerBySlug({ slug: slug.value, makerPage: true });

    if (makersStore.chosenMaker) {
      track("maker_viewed", {
        maker_slug: slug.value,
        maker_name: makersStore.chosenMaker.name || slug.value,
        source: "direct",
      });
    }

    if (route.query.cart) {
      try {
        const data = await ordersStore.getShareCart(route.query.cart as string);
        // `cart` column on the backend is `DataTypes.JSON`, which Sequelize
        // auto-parses on read — so `data.cart` arrives as an object, not a
        // string. Older rows / clients may still produce a stringified value,
        // so accept both shapes.
        const raw = (data as { cart: unknown }).cart;
        const cartData =
          typeof raw === "string" ? JSON.parse(raw) : (raw as Record<string, unknown>);
        if (!cartData || !Array.isArray((cartData as { items?: unknown[] }).items)) {
          throw new Error("shareCart response missing items");
        }
        await ordersStore.recreateCart({
          ...cartData,
          maker: makersStore.chosenMaker,
        });
      } catch (error) {
        console.error("[shareCart] hydration failed", error);
        const { $notify } = useNuxtApp();
        $notify({
          text: "We couldn't load the shared cart link. Starting you a fresh basket.",
          variant: "warning",
        });
        ordersStore.clearCart({ clearLocal: false });
      }
    } else {
      ordersStore.clearCart({ clearLocal: false });
      const stored = localStorage.getItem("and-dine-cart");
      if (stored) ordersStore.setCart({ cart: JSON.parse(stored) });
    }

    makersStore.setFilteredMakers(false);
    // Reviews feed the header's rating, so they have to resolve before the header paints,
    // alongside the maker itself. Tags are independent of both; run the two together
    // rather than serially.
    await Promise.all([productsStore.getTags(), reviewsStore.getReviews()]);
    await makersStore.getDeliveryDays(chosenMaker.value.id);

    // Handles BOTH url forms. This used to inline the `?product={id}` branch only, so
    // a fresh load of a canonical `/store/{maker}/product/{dish}` url opened no modal
    // at all — the customer got the bare SSR dish summary, flat and unclickable. The
    // dedicated watcher below covers dish-to-dish changes but is not `immediate`, so
    // it never fired on first paint either.
    await syncProductFromRoute();
  } finally {
    // Release the gate even if a fetch threw — a failed load must fall through to the
    // page's own empty state, never leave the customer on a permanent spinner.
    pageLoading.value = false;
  }

  nextTick(() => {
    measureHeights();
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);
    handleScroll();
  });

  // Web fonts change text metrics, so a bio measured against the fallback font can
  // wrap onto a different number of lines once the real font lands — re-measure then
  // or a long bio can end up with no "Read more".
  document.fonts?.ready.then(measureBioOverflow);
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("resize", handleResize);
  // The chips remount unselected, so drop the filter they set or it outlives them.
  productsStore.setFilteredProductCategories(false);
});

// Watch for slug changes (navigating between stores)
watch(slug, async (newSlug) => {
  if (!newSlug) return;
  showProductModal.value = false;
  showMakerModal.value = false;
  showShareModal.value = false;
  showVerifyModal.value = false;
  selectedTemperature.value = null;

  // Maker-to-maker navigation needs the same gate as first load. Without it the
  // previous maker's banner and rating stay on screen against the new URL until
  // getMakerBySlug happens to flip the store's own flag a tick later.
  pageLoading.value = true;
  try {
    await makersStore.getMakerBySlug({ slug: newSlug, makerPage: true });
    ordersStore.clearCart({ clearLocal: false });
    const stored = localStorage.getItem("and-dine-cart");
    if (stored) ordersStore.setCart({ cart: JSON.parse(stored) });
    makersStore.setFilteredMakers(false);
    await Promise.all([productsStore.getTags(), reviewsStore.getReviews()]);
    await makersStore.getDeliveryDays(chosenMaker.value.id);

    // Both url forms — see the note on the same call in `onMounted`.
    await syncProductFromRoute();
  } finally {
    pageLoading.value = false;
  }
});

function measureBioOverflow() {
  const el = bioTextRef.value;
  if (!el) {
    bioOverflowing.value = false;
    return;
  }
  // Only measurable while the clamp class is on — expanded text never overflows, so
  // re-measuring then would hide "Read less" and trap the customer in the long bio.
  if (bioExpanded.value) return;
  // 1px tolerance: sub-pixel line-height rounding makes scrollHeight exceed
  // clientHeight by a fraction on bios that fit exactly within the clamp.
  bioOverflowing.value = el.scrollHeight - el.clientHeight > 1;
}

// The bio only exists in the DOM once the maker resolves (the page renders a spinner
// until then), so onMounted alone is too early to measure. Watching the template ref
// itself catches the paragraph appearing — including on maker-to-maker navigation,
// where the spinner unmounts it and remounts it with new text.
// `flush: "post"` so the paragraph has re-rendered with the new text before we measure.
watch(
  [bioTextRef, () => chosenMaker.value.description],
  () => {
    bioExpanded.value = false;
    measureBioOverflow();
  },
  { flush: "post" },
);

// Opens (or closes) the product modal to match the URL. Two entry points:
//   /store/{maker}/product/{dish}  — canonical, resolved from the maker payload
//   /store/{maker}?product={id}    — legacy shared links, 301'd to the path form
// where the product has a usable slug.
async function syncProductFromRoute() {
  const legacyId = route.query.product as string | undefined;

  if (legacyId) {
    const matched = findProductById(makersStore.chosenMaker?.productCategories, legacyId);
    if (matched && hasCanonicalProductPath(matched)) {
      // Server-side navigation already 301s this (see setup). Reaching here means a
      // client-side arrival, so replace the URL without adding a history entry.
      await navigateTo(productPath(slug.value, matched), { replace: true });
      return;
    }
    // No usable slug — keep the legacy behaviour so the link still works.
    await productsStore.getProductById(legacyId);
    if (productsStore.chosenProduct && productsStore.chosenProduct.id) {
      openProductModalFor(productsStore.chosenProduct);
    }
    return;
  }

  if (!productSlug.value) {
    showProductModal.value = false;
    return;
  }

  const product = activeProduct.value;
  if (!product) return;

  await productsStore.getProductById(String(product.id));
  if (productsStore.chosenProduct && productsStore.chosenProduct.id) {
    openProductModalFor(productsStore.chosenProduct);
  }
}

// Re-sync whenever the dish in the URL changes without the page remounting — e.g.
// browser back/forward between two dishes, or closing the modal.
watch([productSlug, () => route.query.product], async () => {
  if (makersStore.chosenMaker) await syncProductFromRoute();
});

function openProductModalFor(product: Product) {
  modalQuantity.value = product.min_order_amount || 1;
  minQuantity.value = product.min_order_amount || 1;
  showProductModal.value = true;
}

function measureHeights() {
  measureBioOverflow();
  if (filtersRef.value) {
    filterHeight.value = filtersRef.value.getBoundingClientRect().height;
    if (!filterOffsetTop.value) {
      filterOffsetTop.value = filtersRef.value.getBoundingClientRect().top + window.scrollY;
    }
  }
  if (cartRef.value) {
    cartHeight.value = cartRef.value.getBoundingClientRect().height;
    if (!cartOffsetTop.value) {
      cartOffsetTop.value = cartRef.value.getBoundingClientRect().top + window.scrollY;
    }
  }
}

function handleScroll() {
  const rem = parseFloat(getComputedStyle(document.documentElement).fontSize || "16");
  const nav = document.querySelector(".navbar") as HTMLElement | null;
  const navHeight = nav?.offsetHeight || Math.round(5 * rem);

  // --- Filter bar fixing ---
  if (filtersRef.value) {
    if (filterOffsetTop.value > 0) {
      const threshold = filterOffsetTop.value - navHeight;
      if (window.scrollY >= threshold) {
        if (!isFilterFixed.value && !(isMobile.value && ordersStore.showCart)) {
          filterHeight.value = filtersRef.value.getBoundingClientRect().height;
          isFilterFixed.value = true;
        }
      } else {
        isFilterFixed.value = false;
      }
    }
  }

  // --- Cart top position ---
  const filterTopPx = Math.round(5 * rem);
  const gapPx = filterGap;
  const footerGapPx = 2 * rem; // minimum 2rem gap between cart and footer
  cartTopPx.value = isFilterFixed.value
    ? filterTopPx + filterHeight.value + gapPx
    : navHeight + gapPx;

  // --- Cart fixing ---
  if (cartRef.value) {
    if (cartOffsetTop.value > 0) {
      const threshold = cartOffsetTop.value - cartTopPx.value;
      if (window.scrollY >= threshold) {
        if (!isCartFixed.value) {
          const rect = cartRef.value.getBoundingClientRect();
          cartHeight.value = rect.height;
          // Sub-pixel precision matters: the spacer that takes the cart's place
          // is width: 33% (real percentage), so any rounding here shifts the cart
          // off the spacer's left edge and the gap to the menu visibly shrinks/grows.
          cartLeftPx.value = rect.left;
          cartWidthPx.value = rect.width;
          isCartFixed.value = true;
        }
      } else {
        isCartFixed.value = false;
        cartStopped.value = false;
      }
    }

    // Prevent cart from overlapping footer — stop the cart in place
    if (isCartFixed.value && !cartStopped.value) {
      const footer = document.querySelector(".footer") as HTMLElement | null;
      if (footer) {
        const footerTop = footer.getBoundingClientRect().top;
        const cartEl = cartRef.value;
        const cartBottom = cartEl.getBoundingClientRect().bottom;
        if (cartBottom >= footerTop - footerGapPx) {
          const wrapperTop = makersRef.value
            ? makersRef.value.getBoundingClientRect().top + window.scrollY
            : 0;
          cartStoppedTop.value =
            footerTop + window.scrollY - wrapperTop - cartEl.offsetHeight - footerGapPx;
          cartStopped.value = true;
          cartHeight.value = cartEl.offsetHeight;
        }
      }
    }
    // Keep cartStoppedTop in sync if cart height changes (e.g. accordion)
    if (cartStopped.value) {
      const footer = document.querySelector(".footer") as HTMLElement | null;
      if (footer) {
        const cartEl = cartRef.value;
        const wrapperTop = makersRef.value
          ? makersRef.value.getBoundingClientRect().top + window.scrollY
          : 0;
        const footerTop = footer.getBoundingClientRect().top + window.scrollY;
        cartStoppedTop.value = footerTop - wrapperTop - cartEl.offsetHeight - footerGapPx;
        cartHeight.value = cartEl.offsetHeight;

        // If scrolling back up, re-fix the cart
        const fixedTop = cartTopPx.value;
        const fixedBottom = fixedTop + cartEl.offsetHeight;
        if (footer.getBoundingClientRect().top > fixedBottom + 20) {
          cartStopped.value = false;
        }
      }
    }
  }

  const handledObject = findLastScrolled();
  if (handledObject.id) lastScrolled.value = handledObject;
}

function handleResize() {
  // A narrower column wraps the bio onto more lines, so a bio that fit the clamp on
  // desktop can overflow on mobile (and vice versa) — re-measure on every resize.
  measureBioOverflow();
  if (cartRef.value) {
    const rect = cartRef.value.getBoundingClientRect();
    cartLeftPx.value = Math.round(rect.left);
    cartWidthPx.value = Math.round(rect.width);
  }
  handleScroll();
}

function findLastScrolled() {
  const elements = scrollContainerRef.value?.children;
  let lastId = null;
  let lastIndex = null;
  if (elements) {
    // Account for the fixed navbar + filter bar so that elements hidden
    // behind those fixed headers are not considered "visible".
    const nav = document.querySelector(".navbar") as HTMLElement | null;
    const navH = nav?.offsetHeight ?? 80;
    const filterH = isFilterFixed.value ? filterHeight.value : 0;
    const topThreshold = navH + filterH + 16;

    let catIdx = 0;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLElement;
      // Skip non-category children (e.g. bundles div) — they have no id
      if (!el.id) continue;
      const rect = el.getBoundingClientRect();
      // A section becomes "current" once its top scrolls to or above the
      // header threshold. Keep overwriting so we end up with the last
      // (most recently scrolled-past) section — standard scroll-spy.
      if (rect.top <= topThreshold) {
        lastId = el.id;
        lastIndex = catIdx;
      }
      catIdx++;
    }

    // If no section has scrolled past the threshold yet (page is at the
    // very top), pick the first section.
    if (!lastId) {
      catIdx = 0;
      for (let i = 0; i < elements.length; i++) {
        const el = elements[i] as HTMLElement;
        if (!el.id) continue;
        lastId = el.id;
        lastIndex = catIdx;
        break;
      }
    }
  }
  return { id: lastId, index: lastIndex };
}

function openProductModal(payload: any) {
  const product = payload.product;
  const minOrder = Number(product.min_order_amount) || Number(payload.quantity) || 1;
  // Modifier-less products only ever have one cart line — so reopening the
  // modal should reflect what's already in the cart. Otherwise `mode: "set"`
  // in addToCart would silently overwrite a cart of (say) 3 down to 1 the
  // moment the customer hits Add to cart again.
  // Products with modifier groups can have multiple variants in the cart, so
  // we keep the "fresh variant at min" behaviour for them.
  const hasModifiers = Array.isArray(product.modifierGroups) && product.modifierGroups.length > 0;
  let initialQuantity = Number(payload.quantity) || minOrder;
  if (!hasModifiers) {
    const cartObj = ordersStore.cart as Record<string, unknown> | false;
    const cartItems = cartObj && typeof cartObj === "object" ? (cartObj.items as any[]) : [];
    const cartItem = cartItems?.find((item: any) => item.product?.id === product.id);
    if (cartItem && Number(cartItem.quantity) > 0) {
      initialQuantity = Number(cartItem.quantity);
    }
  }
  modalQuantity.value = initialQuantity;
  minQuantity.value = minOrder;
  productsStore.getProductById(payload.product.id).then(() => {
    if (!productsStore.chosenProduct || !productsStore.chosenProduct.id) return;
    track("product_viewed", {
      product_id: payload.product.id,
      product_name: payload.product.name || productsStore.chosenProduct.name,
      maker_slug: slug.value,
      price_gross: payload.product.price_gross,
    });
    // Give the dish its own canonical URL. `productPath` falls back to the legacy
    // ?product={id} form when the slug is unusable, so this is always addressable.
    const target = productPath(slug.value, payload.product);
    if (route.fullPath !== target) router.push(target);
    showProductModal.value = true;
  });
}

async function closeProductModal() {
  showProductModal.value = false;
  modalQuantity.value = 1;
  minQuantity.value = 1;
  selectedModifiers.value = [];
  // Back to the maker page, dropping both the /product/{slug} segment and any
  // legacy ?product= query.
  //
  // Navigate BEFORE clearing `chosenProduct`: that flag going false is what un-hides
  // the nested product route's server-side dish summary, so clearing it while that
  // route is still mounted flashes the flat summary for a frame on every close.
  await router.push(`/store/${slug.value}`);
  productsStore.setChosenProduct(false);
}

function updateModalQuantity(payload: any) {
  const previousQuantity = modalQuantity.value;
  const nextQuantity = payload.product.quantity;
  modalQuantity.value = nextQuantity;
  if (nextQuantity < previousQuantity && dishModifiers.value) {
    selectedModifiers.value = clampSplitQuantityModifiers(
      selectedModifiers.value,
      dishModifiers.value,
      nextQuantity,
    );
  }
}

function updateFromPage(payload: any) {
  const newItem = {
    product: payload.product,
    quantity:
      payload.product.quantity < payload.product.min_order_amount
        ? payload.product.min_order_amount
        : payload.product.quantity,
    // Picked -> line totals on the way into the cart. See utils/lineModifierQuantities.ts.
    appliedModifiers: scaleModifiersToLine(
      selectedModifiers.value,
      payload.product.quantity < payload.product.min_order_amount
        ? payload.product.min_order_amount
        : payload.product.quantity,
    ),
    maker: chosenMaker.value,
  };
  if (ordersStore.cart?.maker && ordersStore.cart.maker.slug !== route.params.slug) {
    pendingItem.value = { ...newItem, date: ordersStore.deliveryFilter || false };
    showMakerModal.value = true;
  } else if (payload.product.quantity >= payload.product.min_order_amount) {
    ordersStore.addToCart({ ...newItem, date: ordersStore.cart?.date || false, mode: "set" });
  } else {
    ordersStore.removeFromCart({
      product: payload.product,
      quantity: 0,
      appliedModifiers: payload.appliedModifiers,
      maker: ordersStore.cart?.maker,
      date: ordersStore.cart?.date || false,
    });
  }
}

function updateFromModal(payload: any) {
  // Per-unit pence, matching product.price_gross. This figure is BAKED INTO the cart item, so a wrong
  // value persists into CartSummary rather than just the modal.
  //
  // Per-unit is the line total divided by the line quantity, an identity that holds for both branches
  // of the split rule (see modifiersLineTotal). Doing it this way means the split-vs-picked decision
  // lives in exactly one place; the previous inline sum multiplied a split share once per unit.
  const lineQuantity = Number(payload.quantity) || 1;
  const modsPrice =
    modifiersLineTotal(
      selectedModifiers.value as any[],
      lineQuantity,
      (item: any) => parseFloat(String(item.price_gross || 0)) * 100,
    ) / lineQuantity;
  const newItem = {
    product: {
      ...payload.product,
      price_gross: payload.product.price_gross + modsPrice,
    },
    quantity: payload.quantity,
    // Picked -> line totals on the way into the cart. See utils/lineModifierQuantities.ts.
    appliedModifiers: scaleModifiersToLine(selectedModifiers.value, payload.quantity),
    maker: chosenMaker.value,
  };
  if (ordersStore.cart?.maker && ordersStore.cart.maker.slug !== route.params.slug) {
    pendingItem.value = newItem;
    showMakerModal.value = true;
  } else {
    ordersStore
      .addToCart({ ...newItem, date: ordersStore.cart?.date || false, mode: "set" })
      .then(() => {
        selectedModifiers.value = [];
      });
    closeProductModal();
  }
}

// groupId is passed in from the v-for rather than read off the payload: OptionList
// drops zero-quantity entries before emitting, so unticking the last modifier in a
// group emits [] and `modifiers[0]?.group_id` was undefined. The old early return
// then left the stale selection in place — the price never dropped, and the removed
// add-on was still sent to the cart and charged.
function updateModifiers(modifiers: any[], groupId: number) {
  selectedModifiers.value = selectedModifiers.value
    .filter((a) => a.modifier_group_id !== groupId)
    .concat(
      modifiers
        .filter((a) => a.quantity > 0)
        .map((a) => ({
          // The param, not `a.group_id`: the filter above keys off `groupId`, and one source for
          // one field is what stops the filter missing and stale entries accumulating.
          modifier_group_id: groupId,
          modifier_id: a.id,
          quantity: a.quantity,
          split_quantity: a.split_quantity,
          name: a.name,
          allergens: a.allergens,
          price_gross: parseFloat(String(a.price_gross / 100)).toFixed(2),
          is_vegetarian: a.is_vegetarian,
          is_vegan: a.is_vegan,
          is_halal: a.is_halal,
          is_gluten_free: a.is_gluten_free,
          is_nut_free: a.is_nut_free,
          is_dairy_free: a.is_dairy_free,
        })),
    );
}

function clearCurrentCart() {
  // Capture the in-flight order id BEFORE clearing — once clearCart runs
  // the store's `order` ref is gone and we can't recover it for the
  // localStorage cleanup below.
  const staleOrderId = (ordersStore.order as { id?: string | number } | false)
    ? (ordersStore.order as { id?: string | number }).id
    : null;

  ordersStore.clearCart();
  // The customer has confirmed switching makers, which abandons any
  // mid-flight checkout on the previous maker. Drop the active-checkout
  // sessionStorage pointer + per-order localStorage entry so the next
  // checkout starts clean rather than resuming the old one.
  ordersStore.clearActiveCheckout();
  if (staleOrderId && import.meta.client) {
    try {
      localStorage.removeItem(String(staleOrderId));
    } catch {
      // Storage disabled / quota — non-fatal.
    }
  }

  if (pendingItem.value) ordersStore.addToCart(pendingItem.value);
  showMakerModal.value = false;
  showProductModal.value = false;
  pendingItem.value = false;
}

function filterProductCategories(allergens: any[]) {
  productsStore.setFilteredProductCategories(allergens.length > 0 ? allergens : false);
}

function setTemperatureFilter(value: boolean | null) {
  selectedTemperature.value = value;
}

function toggleShareModal() {
  showShareModal.value = !showShareModal.value;
}

function copyLink() {
  const config = useRuntimeConfig();
  const baseUrl = (config.public.mainUrl as string) || "https://anddine.co.uk";
  navigator.clipboard.writeText(`${baseUrl}${route.path}`);
  isCopied.value = true;
  setTimeout(() => {
    isCopied.value = false;
  }, 1000);
}
</script>

<template>
  <section>
    <Loading v-if="pageLoading || makersStore.makerLoading" />
    <div
      v-else
      class="maker-store"
    >
      <NuxtLink
        class="maker-store__back"
        to="/search"
      >
        <fa
          class="small-icon"
          :icon="['fal', 'arrow-left']"
        />
        Back to Makers
      </NuxtLink>
      <div class="maker-store__header">
        <img
          class="maker-store__hero"
          loading="eager"
          decoding="async"
          :src="
            chosenMaker?.attachments?.banner?.length > 0
              ? attachmentUrl(chosenMaker.attachments.banner, 'banner')
              : staticUrl('/Images/site-images/placeholder.png')
          "
          :alt="chosenMaker.name || 'Maker banner'"
        />
        <div class="maker-store__bio">
          <div class="maker-store__bio--title">
            <!-- On a dish URL the product is the subject of the page, so it owns
                 the h1 (see the child route) and the maker name steps down to h2.
                 Exactly one h1 per page. -->
            <component
              :is="productSlug ? 'h2' : 'h1'"
              class="grey-text"
            >
              {{ chosenMaker.name }}
            </component>
            <div
              v-if="reviewCount || makerIsNew"
              class="rating"
            >
              <template v-if="reviewCount">
                <span
                  class="rating__star"
                  aria-hidden="true"
                >
                  ★
                </span>
                <span class="rating__score light-grey-text">
                  {{ Number(reviewCount).toFixed(1) }}
                </span>
              </template>
              <Pill
                v-if="makerIsNew"
                text="NEW"
                color="dark"
                type="filled"
                size="large"
              />
            </div>
          </div>
          <div
            v-if="makerAttributeTags.length"
            class="maker-store__bio--attributes"
          >
            <Badge
              v-for="tag in makerAttributeTags"
              :key="tag.id"
              :variant="attributeTagVariant(tag)"
              size="lg"
            >
              {{ tag.name }}
            </Badge>
          </div>
          <div class="maker-store__bio--description">
            <p
              ref="bioTextRef"
              class="light-grey-text"
              :class="{ 'maker-store__bio--description-clamped': !bioExpanded }"
              v-html="chosenMaker.description"
            ></p>
            <button
              v-if="chosenMaker.description && bioOverflowing"
              type="button"
              class="maker-store__bio--description-toggle"
              @click="bioExpanded = !bioExpanded"
            >
              {{ bioExpanded ? "Read less" : "Read more about this Maker" }}
            </button>
          </div>
          <!-- Cuisines were rendered on the maker cards that link *here*, but never
               on the destination page — so the strongest topical signal for a store
               page was missing from its own content. Matches `servesCuisine`. -->
          <ul
            v-if="makerCuisines.length"
            class="maker-store__bio--cuisines"
          >
            <li
              v-for="cuisine in makerCuisines"
              :key="cuisine"
              class="light-grey-text"
            >
              {{ cuisine }}
            </li>
          </ul>
        </div>
      </div>
      <!-- Filters (sticky). Hidden while the mobile basket overlay is open so
           the cart owns the full viewport below the nav on mobile (same
           guard applied consistently across premium/store/[slug].vue and
           orders/[id]/edit.vue). v-show, not v-if — StoreAllergenFilter
           keeps its selected/hovered/panel state in local refs with no
           restore prop, so unmounting it on every cart open/close would
           silently clear the user's allergen selection. The style below's
           z-index 9999 matches DynamicModal.vue's sitewide modal z-index
           (allergen filter is Teleported there) — intentionally above
           $z-cart-overlay, not part of this file's z-index scale. -->
      <div
        v-show="!(isMobile && ordersStore.showCart)"
        ref="filtersRef"
        class="maker-store__filters"
        :style="
          isFilterFixed
            ? {
                position: 'fixed',
                top: '5rem',
                left: '0',
                right: '0',
                width: '100%',
                margin: '0',
                zIndex: allergenModalVisible ? Z_MODAL : Z_FILTER_FIXED,
              }
            : {}
        "
      >
        <div class="maker-store__filters--row">
          <StoreListFilter
            class="maker-store__filters--carousel"
            :items="categoryItems"
            :last-scrolled="lastScrolled"
            :filter-el="filtersRef"
          />
          <div class="maker-store__filters--separator" />
          <StoreAllergenFilter
            v-if="productsStore.allergens"
            class="maker-store__filters--allergen"
            :results="resultLength"
            :allergens="productsStore.allergens"
            @return-allergens="filterProductCategories"
            @return-show-allergens="allergenModalVisible = $event"
            @return-temperature="setTemperatureFilter"
          />
        </div>
      </div>
      <div
        v-if="!isMobile"
        :style="isFilterFixed ? { height: filterHeight + filterGap + 'px' } : { height: '0' }"
      />
      <!-- Nested product route (store/[slug]/product/[productSlug]). Renders a
           server-side dish summary so the product URL has crawlable content of its
           own; the interactive modal opens over the top on the client. -->
      <NuxtPage />

      <div
        v-if="productsStore.productCategories"
        ref="makersRef"
        class="maker-store__wrapper"
      >
        <div
          v-if="!isMobile || !ordersStore.showCart"
          ref="scrollContainerRef"
          class="maker-store__wrapper--menu"
        >
          <div
            v-if="productsStore.visibleBundles.length > 0"
            class="bundles"
          >
            <h2 class="grey-text title">{{ chosenMaker.name }} Set Menus</h2>
            <ClientOnly>
              <CarouselBase
                :breakpoints="[
                  [300, 1],
                  [600, 2],
                  [1024, 3],
                ]"
              >
                <CarouselSlide
                  v-for="(bundle, bIdx) in productsStore.visibleBundles"
                  :key="`bundle_${bIdx}`"
                >
                  <StoreBundleList
                    :bundle="bundle"
                    :maker="chosenMaker"
                    :is-lazy="true"
                    :is-carousel="true"
                    @return-bundle="openProductModal"
                  />
                </CarouselSlide>
              </CarouselBase>
            </ClientOnly>
          </div>
          <div
            v-for="(category, index) in finalFilteredCategories"
            :id="
              category.slug
                .toLowerCase()
                .replace(/[^\w\s]/gi, '')
                .trim()
                .replace(/\s+/g, '-')
            "
            :key="`filter_${index}`"
          >
            <h2
              v-if="
                category.products.length > 0 &&
                category.products.find((product: any) => product.is_active)
              "
              class="grey-text title"
              :style="index === 0 && 'margin-top: 0;'"
            >
              {{ category.name }}
            </h2>
            <div class="products">
              <ProductList
                v-for="(product, i) in category.products.filter((p: any) => p.is_active)"
                :key="`${product.slug}_${i}`"
                :product="product"
                :maker-name="chosenMaker.name"
                :cart-loading="ordersStore.cartLoading"
                :cart-item="
                  ordersStore.cart?.items?.find((item: any) => item.product.id === product.id) ||
                  false
                "
                :quantity="
                  ordersStore.cart?.items
                    ?.filter((item: any) => item.product.id === product.id)
                    .reduce((acc: number, item: any) => acc + item.quantity, 0) || 0
                "
                @return-product="openProductModal"
                @update-from-page="updateFromPage"
              />
            </div>
          </div>
        </div>
        <!-- Mobile cart (shows when toggled on mobile). Teleported to body so it
             isn't trapped in .maker-store__wrapper's z-index:1 stacking context —
             see NavBar.vue's mobile cart banner for the same pattern/rationale. -->
        <ClientOnly>
          <Teleport to="body">
            <div
              v-if="isMobile && ordersStore.showCart"
              class="maker-store__wrapper--cart"
            >
              <ShoppingCart />
            </div>
          </Teleport>
        </ClientOnly>
        <div
          v-if="!isMobile"
          ref="cartRef"
          class="maker-store__wrapper--cart"
          :style="
            cartStopped
              ? {
                  position: 'absolute',
                  top: pxToRem(cartStoppedTop) + 'rem',
                  right: '0',
                  width: '33%',
                  zIndex: 1,
                }
              : isCartFixed
                ? {
                    position: 'fixed',
                    top: pxToRem(cartTopPx) + 'rem',
                    left: pxToRem(cartLeftPx) + 'rem',
                    width: pxToRem(cartWidthPx) + 'rem',
                    zIndex: allergenModalVisible ? Z_CART_BEHIND_MODAL : Z_CART_FIXED,
                  }
                : {}
          "
        >
          <ShoppingCart />
        </div>
        <!-- Spacer only rendered while the cart is detached from flow. Otherwise
             it's an empty third flex item that still pulls a `gap: 2%` on each
             side, compressing the menu/cart widths and visibly shrinking the
             gap between them at the moment of stick. -->
        <div
          v-if="!isMobile && (isCartFixed || cartStopped)"
          :style="{ width: '33%', height: cartHeight + 'px', flexShrink: 0 }"
        />
      </div>
      <!-- Product modal -->
      <DynamicModal
        v-if="showProductModal && productsStore.chosenProduct"
        class="maker-store__product-modal"
        :full-screen="true"
        @close-modal="closeProductModal"
      >
        <template #body>
          <div class="maker-store__product-modal--content">
            <StoreImageCarousel
              v-if="hasAdditionalImages"
              class="maker-store__product-modal--carousel"
              :images="chosenProduct.attachments?.mainImage"
            />
            <div
              v-if="hasDishImage"
              class="maker-store__product-modal--image"
              :style="`background-image: url('${attachmentUrl(chosenProduct.attachments?.mainImage[0].file_path_big)}')`"
            ></div>
            <h4 class="grey-text">{{ chosenProduct.name }}</h4>
            <p
              class="grey-text small-text"
              v-html="chosenProduct.description"
            ></p>
            <ProductModalAllergens
              :product="chosenProduct"
              part="summary"
              :modifiers="selectedModifiers"
              :maker-name="chosenMaker.name"
            />
            <div v-if="faqs">
              <DynamicAccordion
                title="FAQs"
                :content="faqs"
              />
            </div>
            <div
              v-if="dishModifiers"
              class="modifiers"
            >
              <div
                v-for="(modifier, i) in dishModifiers"
                :key="`modifier_${i}`"
              >
                <div v-if="modifier.modifiers.some((mod: any) => mod.is_active)">
                  <p class="modifiers__title grey-text">
                    {{ modifier.name }}
                    <i class="light-grey-text small-text">(Choose from up to {{ modifier.max }})</i>
                    <span class="pink-text">{{ modifier.min > 0 ? "*" : "" }}</span>
                  </p>
                  <OptionList
                    :key="`option_list_${modifier.id}_${modalQuantity}`"
                    :show-title="true"
                    :options="
                      modifier.modifiers
                        .filter((mod: any) => mod.is_active)
                        .map((item: any) => ({
                          ...item,
                          group_id: modifier.id,
                          split_quantity: modifier.split_quantity === true,
                          quantity:
                            selectedModifiers.find((s: any) => s.modifier_id === item.id)
                              ?.quantity || 0,
                        }))
                    "
                    :choices="modifier.max"
                    :limit="modifier.split_quantity ? modalQuantity : modifier.max"
                    :min-quantity="modifier.min"
                    @return-option="(mods) => updateModifiers(mods, Number(modifier.id))"
                  />
                </div>
              </div>
            </div>
            <ProductModalAllergens
              :product="chosenProduct"
              part="allergens"
              :modifiers="selectedModifiers"
              :maker-name="chosenMaker.name"
            />
            <div v-if="cookingInstructions">
              <DynamicAccordion
                title="Cooking instructions"
                :content="cookingInstructions"
              />
            </div>
            <div v-if="ingredients">
              <DynamicAccordion
                title="Ingredients"
                :content="ingredients"
              />
            </div>
            <div v-if="shelfLife">
              <DynamicAccordion
                title="Shelf life"
                :content="`${shelfLife} days`"
              />
            </div>
            <div v-if="servingSuggestions">
              <DynamicAccordion
                title="Serving suggestions"
                :content="servingSuggestions"
              />
            </div>
            <div v-if="servings">
              <DynamicAccordion
                title="Serving size"
                :content="servings"
              />
            </div>
            <div v-if="pieces">
              <DynamicAccordion
                title="Individual pieces"
                :content="pieces"
              />
            </div>
            <div v-if="additionalInformation">
              <DynamicAccordion
                title="Additional information"
                :content="`${additionalInformation} \n ${storage}`"
              />
            </div>
          </div>
        </template>
        <template #footer>
          <div class="maker-store__product-modal--actions">
            <QuantityOperator
              :product-quantity="modalQuantity"
              :min-quantity="minQuantity"
              :show-bin="false"
              :picker="true"
              class="quantity-operator"
              @return-quantity="updateModalQuantity"
            />
            <DynamicButton
              color="pink"
              width="100%"
              :text="!ordersStore.cartLoading ? `Add to cart: £${combinedProductTotal}` : ''"
              :icon="ordersStore.cartLoading ? 'spinner' : ''"
              :spinning="ordersStore.cartLoading"
              :disabled="addButtonDisabled"
              @click-event="
                updateFromModal({
                  product: chosenProduct,
                  quantity: modalQuantity,
                  appliedModifiers: selectedModifiers,
                })
              "
            />
          </div>
        </template>
      </DynamicModal>
      <!-- Maker modal (new basket) -->
      <DynamicModal
        v-if="showMakerModal && ordersStore.cart !== false"
        :width="isMobile ? '90%' : '40%'"
        class="maker-store__maker-modal"
        @close-modal="showMakerModal = false"
      >
        <template #body>
          <h3 class="maker-store__product-modal--title grey-text">Create new basket?</h3>
          <p class="maker-store__product-modal--body grey-text">
            This will clear your existing basket from
            {{ ordersStore.cart?.maker?.name }} and start a new order
          </p>
          <DynamicButton
            text="New order"
            color="pink"
            width="100%"
            @click-event="clearCurrentCart"
          />
        </template>
      </DynamicModal>
      <!-- Share modal -->
      <DynamicModal
        v-if="showShareModal"
        class="maker-store__share"
        :width="isMobile ? '90%' : '40%'"
        @close-modal="toggleShareModal"
      >
        <template #body>
          <h3 class="grey-text">Share {{ chosenMaker?.name }} menu</h3>
          <p class="grey-text">
            Click the social icons and spread the word to your friends and family
          </p>
          <div class="maker-store__share--icons">
            <fa
              v-if="!isCopied"
              :icon="['fal', 'link']"
              @click="copyLink"
            />
            <p
              v-else
              class="pink-text"
            >
              Copied!
            </p>
          </div>
        </template>
      </DynamicModal>
      <!-- Verify modal -->
      <DynamicModal
        v-if="showVerifyModal"
        class="maker-store__verify"
        :width="isMobile ? '90%' : '40%'"
        @close-modal="showVerifyModal = !showVerifyModal"
      >
        <template #body>
          <h3 class="grey-text">Food Safety Certified</h3>
          <p class="grey-text">
            This Maker has obtained a food safety certificate from an accredited food safety
            training provider.
          </p>
        </template>
      </DynamicModal>
    </div>
  </section>
</template>

<style lang="scss">
.maker-store {
  width: 90%;
  padding: 5rem 5% 2rem;
  background-color: var(--canvas-warm);
  &__header {
    display: flex;
    flex-direction: row;
    gap: 2rem;
    width: 100%;
    // Bottom margin handled by .maker-store__filters' own margin-top so the
    // visual gap above the filter strip matches its margin-bottom.
    margin-bottom: 0;
    align-items: stretch;
    @media (max-width: $tablet) {
      flex-direction: column;
      gap: 1rem;
    }
  }
  &__hero {
    flex: 1 1 0;
    min-width: 0;
    align-self: flex-start;
    aspect-ratio: 5 / 2;
    width: 100%;
    height: auto;
    // Page background tone for the reserved box while the image decodes.
    background-color: var(--canvas-warm);
    object-fit: cover;
    object-position: center;
    border-radius: var(--radius);
    overflow: hidden;
    display: block;
    // Webkit's default scaling can introduce visible grain on
    // downscaled photos in the hero slot now that we share its row
    // with the bio (so the displayed width is smaller than the
    // source). `-webkit-optimize-contrast` hints at a higher-quality
    // resampling path on Chrome/Safari; harmless elsewhere.
    image-rendering: -webkit-optimize-contrast;
    transform: translateZ(0);
    @media (max-width: $tablet) {
      aspect-ratio: 16 / 9;
    }
  }
  &__wrapper {
    display: flex;
    position: relative;
    width: 100%;
    gap: 2%;
    z-index: 1;
    &--cart {
      width: 33%;
      overflow: visible;
      // Mobile: no positioning here — the mobile cart is Teleported to
      // <body> and ShoppingCart/index.vue's own scoped style owns its
      // fixed positioning/sizing/z-index there (single source of truth;
      // this rule used to duplicate/fight it, which is what produced the
      // 2026-08-23 hotfix's specificity hacks). This class-selector still
      // matches the div wherever it lives in the DOM post-Teleport, so
      // desktop's `width: 33%` above still applies unaffected.
    }
    &--menu {
      display: flex;
      flex-direction: column;
      width: 65%;
      @media (max-width: $tablet) {
        width: 100%;
        margin-right: 0;
      }
      .bundles {
        margin: 0 0 3rem -0.3rem;
        .title {
          margin: 1rem 0 1rem 0.5rem;
        }
      }
      .products {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        width: 100%;
        align-content: flex-start;
      }
      .title {
        margin: 2rem 0 1rem;
        line-height: 2rem;
      }
    }
  }
  &__filters {
    // Break out of the .maker-store container's 5% horizontal padding so the
    // white filter strip spans the body width (NOT viewport — the
    // previous `100vw / -50vw+50%` trick included the scrollbar gutter,
    // which pushed the Filters button on the right past the visible
    // edge). Percentages here resolve against .maker-store's content area
    // (90% of body): 111.111% × 90% = body; -5.555% × 90% = -5% of body
    // which exactly cancels the padding offset.
    width: 111.111%;
    margin: 2rem -5.555%;
    padding: 0.5rem 5%;
    background-color: var(--color-white-1);
    position: relative;
    z-index: $z-filter;
    box-sizing: border-box;
    @media (max-width: $tablet) {
      // Mobile: full-bleed white strip (edge-to-edge) with padded
      // inner content. `100vw` + `margin-left: calc(50% - 50vw)` is
      // the safe break-out — body has `overflow-x: hidden` so the
      // 1–2px scrollbar gutter difference (vs. body width) is
      // invisibly clipped.
      width: 100vw;
      margin-left: calc(50% - 50vw);
      margin-right: 0;
      margin-top: 1.5rem;
      margin-bottom: 1.5rem;
      padding: 0.5rem 5%;
    }
    &--row {
      display: flex;
      align-items: center;
      gap: 0;

      @media (max-width: $tablet) {
        flex-direction: column;
        align-items: stretch;
        gap: 0.25rem;
      }
    }
    &--carousel {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      .list-filter__nav--prev,
      .list-filter__nav--next {
        display: none;
      }

      @media (max-width: $tablet) {
        width: 100%;
      }
    }
    &--separator {
      flex-shrink: 0;
      width: 2px;
      height: 2rem;
      background-color: var(--color-grey-3);
      margin: 0 0.75rem;

      @media (max-width: $tablet) {
        display: none;
      }
    }
    &--allergen {
      flex-shrink: 0;
      // Desktop: right-pinned to the end of the row, no extra inset.
      // The earlier 9rem right-pad was producing a huge dead zone on
      // wide layouts and breaking the strip's right edge.
      margin-left: auto;
      .allergen-filter__button {
        min-width: 5rem;
        border-radius: var(--radius);
        padding: 0.6rem 1.2rem;
        font-size: 0.9rem;
      }
      @media (max-width: $tablet) {
        // Mobile: drop the right-pin, take the full column width, and
        // render the button at 90% centered so it sits in the strip's
        // full padded area on a one-row layout below the scroller.
        margin-left: 0;
        padding: 0 0 0.2rem;
        align-self: flex-start;
        width: 100%;
        .allergen-filter__button {
          justify-content: center;
          width: 90%;
        }
      }
    }
  }
  // Back link — foreground ink, primary on hover (no purple, no pink idle).
  // Margin matches the section gaps used elsewhere on the page so
  // vertical rhythm is consistent.
  &__back {
    display: inline-flex;
    align-items: center;
    margin: 2rem 0 1.5rem;
    color: var(--color-grey-1) !important;
    transition: color var(--dur-fast) var(--ease-out);
    svg {
      margin-right: 0.5rem;
    }
    &:hover {
      color: var(--color-pink-1) !important;
      text-decoration: none;
    }
  }
  &__bio {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.75rem;
    @media (max-width: $tablet) {
      justify-content: flex-start;
    }
    &--title {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      // Refined rating: single warm-amber star + one-decimal score in muted
      // grey. The star is sized to 1rem so it sits inline with the H1 cap-
      // height; gap-2 (0.5rem) separates star from score.
      .rating {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 1rem;
        color: var(--color-grey-2);
        line-height: 1;
        span {
          margin: 0 !important;
          line-height: 1 !important;
        }
        &__star {
          color: var(--color-amber-1);
          font-size: 1rem;
          line-height: 1;
        }
        &__score {
          font-size: 1rem;
          font-weight: var(--weight-medium);
        }
      }
    }
    &--description {
      &-clamped {
        display: -webkit-box;
        -webkit-line-clamp: 5;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    }
    // Read more / Read less — foreground ink with primary on hover (no purple).
    // !important guards against browser default :visited (browsers can show
    // anchors as purple when they share an href history) and any global rule
    // drift. Style-guide: no purple anywhere.
    &--cuisines {
      display: none;
      flex-wrap: wrap;
      gap: 0.5rem;
      list-style: none;
      margin: 0.75rem 0 0;
      padding: 0;

      li {
        border: 1px solid var(--color-grey-3, #e0e0e0);
        border-radius: 1rem;
        padding: 0.2rem 0.75rem;
        font-size: 0.85rem;
      }
    }

    &--attributes {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
      margin: 0.75rem 0 0;
    }

    &--description-toggle {
      appearance: none;
      background: none;
      border: 0;
      padding: 0;
      margin: 0;
      cursor: pointer;
      font-size: 1rem;
      font-weight: var(--weight-semibold);
      font-family: inherit;
      color: var(--color-grey-1) !important;
      transition: color var(--dur-fast) var(--ease-out);
      &:hover {
        color: var(--color-pink-1) !important;
        text-decoration: underline;
      }
    }
  }
  &__suggested {
    margin-top: 3rem !important;
  }
  &__suggested,
  &__reviews {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: 0;
    @media (max-width: $tablet) {
      margin-bottom: 50px;
    }
    &--similar,
    &--top {
      position: relative;
      padding: 0 6.5%;
    }
  }
  &__same-day-modal {
    &--body {
      margin: 0.6rem 0 var(--radius);
    }
  }
  &__product-modal {
    &--carousel {
      .slide {
        &__image {
          border-radius: 0.4rem;
          width: 100%;
          max-height: 30vh;
          margin-bottom: 1rem;
        }
      }
    }
    &--image {
      border-radius: 0.4rem;
      width: 100%;
      height: 30vh;
      background-position: center;
      background-size: cover;
      background-repeat: no-repeat;
      margin-bottom: 1rem;
    }
    &--text {
      display: flex;
      flex-direction: column;
      position: absolute;
      margin: 20px 0;
      height: 20%;
      width: 90%;
      justify-content: center;
      h4 {
        color: var(--color-grey-1);
      }
    }
    &--content {
      .subtitle {
        margin-bottom: 1.5rem;
      }
      .modifiers {
        margin: 20px 0;
        &__title {
          background-color: var(--color-grey-3);
          padding: 1rem 6%;
          margin: 0 -6%;
        }
      }
    }
    &--actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-direction: column;
      @media (max-width: $tablet) {
        flex-direction: column;
        text-align: center;
      }
      .quantity-operator {
        margin: 0 0 10px;
      }
    }
  }
  &__share {
    &--icons {
      display: flex;
      margin: 20px 0 20px 0;
      justify-content: space-evenly;
      // Share icons follow the icon rule — foreground ink, pink only on hover.
      svg {
        font-size: 30px;
        color: var(--color-grey-1);
        cursor: pointer;
        transition:
          transform 0.2s ease,
          color 0.2s ease;
        &:hover {
          color: var(--color-pink-1);
          transform: scale(1.02);
        }
      }
    }
  }
  .sticky-cart {
    position: sticky;
    z-index: 8888;
    top: 12rem !important;
    right: 5%;
    margin: 0;
    width: 31.5%;
    background: var(--color-white-1);
    border-radius: 0.4rem;
  }
  .sticky-filter {
    position: sticky;
    z-index: 7777;
    left: 0;
    width: 90%;
    top: 5rem;
    padding: 1rem 5%;
  }
  .sticky-maker {
    padding-top: 15rem;
  }
}
</style>
